import {useCallback, useEffect, useMemo, useState} from 'react';

import {useAnnotatableContentPresence, useAnnotationModeStore} from './AnnotationModeStore';
import {buildAnchor, isAnchoredInLang, isMarkableRange} from './annotationAnchors';
import {combinedNoteOf, computeAnnotationMerge, findContainingAnnotation} from './annotationMerge';
import {exceedsNoteLimit} from './annotationRules';
import {selectVisibleRegionAnnotations} from './annotationSelectors';
import {
  clearRegionHighlights,
  hasRegionHighlights,
  setRegionHighlights
} from './annotationPaintCoordinator';
import type {TextProjection} from './annotationTextProjection';
import type {AnnotationAnchor, AnnotationMerge, AnnotationRange} from './annotationTypes';
import {
  activeSelectionRange,
  annotationAnchorAtPoint,
  findAnnotationAtPoint,
  getSelectionNormalizedRange,
  hasUntypesetMath,
  isHighlightApiSupported,
  resolveAnnotationRanges,
  selectionCoversNestedRegion,
  selectionCoversTextOutside
} from './annotationUtils';
import {useRegionEdit} from './useRegionEdit';
import {useTextProjection} from './useTextProjection';

/**
 * `containerRef` is a callback ref so the effects re-run if the element
 * attaches late (e.g. the feature flag resolving after first render).
 */
export function useAnnotations(
  regionId: string,
  lang: string | null = null
): {
  captureAnnotation: (x: number, y: number) => AnnotationAnchor | null;
  getAnnotationAtPoint: (x: number, y: number) => AnnotationAnchor | null;
  containerRef: (element: HTMLElement | null) => void;
  /** The region element once it has attached; null before that. */
  container: HTMLElement | null;
  getProjection: () => TextProjection | null;
  isMergeConfirmPending: boolean;
  /** True when the pending merge's combined note is longer than the server accepts. */
  isMergeNoteTooLong: boolean;
  /** Applies the pending merge; returns the merged mark so its popover can open. */
  confirmPendingMerge: () => AnnotationAnchor | null;
  cancelPendingMerge: () => void;
} {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [pendingMerge, openPendingMerge, cancelPendingMerge] = useRegionEdit<{
    merge: AnnotationMerge;
    rect: DOMRect;
  }>();
  const storedAnnotations = useAnnotationModeStore((state) =>
    selectVisibleRegionAnnotations(state, regionId)
  );
  // `repaint` depends on this: a fresh array each render would rebuild every
  // Highlight in the document.
  const annotations = useMemo(
    () => storedAnnotations.filter(({anchor}) => isAnchoredInLang(anchor, lang)),
    [storedAnnotations, lang]
  );
  const isMergeNoteTooLong =
    pendingMerge !== null && exceedsNoteLimit(combinedNoteOf(annotations, pendingMerge.merge));
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  if (!isAnnotationModeOn && pendingMerge) {
    cancelPendingMerge();
  }
  const addAnnotation = useAnnotationModeStore((state) => state.addAnnotation);
  const mergeAnnotations = useAnnotationModeStore((state) => state.mergeAnnotations);
  useAnnotatableContentPresence(Boolean(container?.textContent?.trim()));

  const getProjection = useTextProjection(container, {
    onMutate: () => clearRegionHighlights(regionId),
    // Deferred closure: `repaint` is declared below; the hook reads handlers
    // through a ref, so the latest one always fires.
    onSettle: () => repaint()
  });

  const repaint = useCallback(() => {
    if (!container || !isHighlightApiSupported()) {
      return;
    }
    const projection = annotations.length > 0 ? getProjection() : null;
    setRegionHighlights(
      regionId,
      projection ? resolveAnnotationRanges(projection, annotations) : new Map()
    );
  }, [container, regionId, annotations, getProjection]);

  /** The mark this selection lands on, applying the #19010 overlap policy. */
  const addOrMergeAnnotation = useCallback(
    (projection: TextProjection, range: AnnotationRange, rect: DOMRect): string | null => {
      // Each gesture answers for itself; a question left over from an earlier one
      // must never outlive it.
      cancelPendingMerge();
      const containing = findContainingAnnotation(projection, annotations, range, lang);
      if (containing) return containing.id;

      const merge = computeAnnotationMerge(projection, annotations, range, lang);
      if (merge) {
        if (merge.absorbedIds.length > 0) {
          openPendingMerge({merge, rect}, regionId);
          return null;
        }
        mergeAnnotations(merge);
        return merge.intoId;
      }

      const anchor = buildAnchor(projection, range, {regionId, lang});
      return anchor && addAnnotation(anchor);
    },
    [
      annotations,
      lang,
      regionId,
      addAnnotation,
      mergeAnnotations,
      openPendingMerge,
      cancelPendingMerge
    ]
  );

  const captureAnnotation = useCallback(
    (x: number, y: number): AnnotationAnchor | null => {
      // Selection first, so a plain click never pays for a projection build.
      const selection = activeSelectionRange();
      if (!selection || !container) return null;
      // A drag across a region boundary — out of this one, or into a nested one —
      // is rejected outright, never clamped to the part this region happens to cover.
      if (selectionCoversTextOutside(selection, container)) return null;
      if (selectionCoversNestedRegion(selection, container)) return null;
      const projection = getProjection();
      const range = projection && getSelectionNormalizedRange(projection);
      if (!projection || !range || !isMarkableRange(projection, range)) return null;

      // The popover hangs below its anchor, so it anchors to the release point:
      // a selection's own box can be taller than the viewport.
      const rect = new DOMRect(x, y);
      const id = addOrMergeAnnotation(projection, range, rect);
      if (!id) return null;
      // Consume the selection only when the gesture landed, so a failed capture
      // stays selectable for copying.
      window.getSelection()?.removeAllRanges();
      return {rect, id};
    },
    [container, getProjection, addOrMergeAnnotation]
  );

  const confirmPendingMerge = useCallback((): AnnotationAnchor | null => {
    if (!pendingMerge || isMergeNoteTooLong) {
      return null;
    }
    mergeAnnotations(pendingMerge.merge);
    window.getSelection()?.removeAllRanges();
    cancelPendingMerge();
    return {rect: pendingMerge.rect, id: pendingMerge.merge.intoId};
  }, [pendingMerge, isMergeNoteTooLong, mergeAnnotations, cancelPendingMerge]);

  const getAnnotationAtPoint = useCallback(
    (x: number, y: number): AnnotationAnchor | null => {
      const projection = annotations.length > 0 ? getProjection() : null;
      if (!container || !projection) {
        return null;
      }
      const id = findAnnotationAtPoint(container, projection, x, y, annotations);
      return id ? annotationAnchorAtPoint(x, y, id) : null;
    },
    [container, getProjection, annotations]
  );

  useEffect(() => {
    // With nothing painted yet, MathJax rewrites are waited out (no flash
    // against stale DOM); once painted, annotation changes always resync.
    if (container && (hasRegionHighlights(regionId) || !hasUntypesetMath(container))) {
      repaint();
    }
  }, [container, regionId, repaint]);

  // The region's paint dies with its container (unmount, region switch, detach).
  useEffect(() => {
    if (!container) {
      return undefined;
    }
    return () => clearRegionHighlights(regionId);
  }, [container, regionId]);

  return {
    captureAnnotation,
    getAnnotationAtPoint,
    containerRef: setContainer,
    container,
    getProjection,
    isMergeConfirmPending: isAnnotationModeOn && pendingMerge !== null,
    isMergeNoteTooLong,
    confirmPendingMerge,
    cancelPendingMerge
  };
}
